// Comprehensive solar asset manufacturer & model data
// Asset Types: MOD (Module/Panel), INV (Inverter), SCB (String Combiner Box),
//              TRF (Transformer), CAB (Cable), MTB (Mounting/Tracker), SWG (Switchgear/Panel)

export type AssetType = 'MOD' | 'INV' | 'SCB' | 'TRF' | 'CAB' | 'MTB' | 'SWG';

export interface ModelEntry {
  name: string;
  description?: string;
  assetType: AssetType;
  specifications?: {
    capacity?: number;
    voltage?: number;
    power?: number;
    efficiency?: number;
  };
}

export interface ManufacturerEntry {
  name: string;
  description: string;
  website: string;
  contactInfo?: { email?: string; phone?: string };
  models: ModelEntry[];
}

export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  MOD: 'Solar Module / Panel',
  INV: 'Inverter',
  SCB: 'String Combiner Box',
  TRF: 'Transformer',
  CAB: 'Cable',
  MTB: 'Mounting Structure / Tracker',
  SWG: 'Switchgear / Control Panel',
};

export const ASSET_TYPE_COLORS: Record<AssetType, { bg: string; text: string; border: string }> = {
  MOD: { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' },
  INV: { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' },
  SCB: { bg: '#F0FDF4', text: '#15803D', border: '#BBF7D0' },
  TRF: { bg: '#FDF4FF', text: '#7E22CE', border: '#E9D5FF' },
  CAB: { bg: '#FFFBEB', text: '#B45309', border: '#FDE68A' },
  MTB: { bg: '#F0F9FF', text: '#0369A1', border: '#BAE6FD' },
  SWG: { bg: '#FFF1F2', text: '#BE123C', border: '#FECDD3' },
};

export const solarManufacturerData: ManufacturerEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  // MOD – Solar Modules / Panels
  // ─────────────────────────────────────────────────────────────────────
  {
    name: 'Jinko Solar',
    description: 'World leading solar module manufacturer based in China, known for Tiger and Eagle series.',
    website: 'https://www.jinkosolar.com',
    contactInfo: { email: 'info@jinkosolar.com' },
    models: [
      { name: 'Tiger Neo N-type 54HL4R-V 490W', assetType: 'MOD', description: 'N-type bifacial module 490W', specifications: { power: 490, efficiency: 22.27 } },
      { name: 'Tiger Neo N-type 66TR-V 580W', assetType: 'MOD', description: 'N-type bifacial module 580W', specifications: { power: 580, efficiency: 22.53 } },
      { name: 'Tiger Neo N-type 78TR-V 630W', assetType: 'MOD', description: 'N-type bifacial large format 630W', specifications: { power: 630, efficiency: 22.63 } },
      { name: 'Eagle G4 72M 340W', assetType: 'MOD', description: 'Poly module 340W', specifications: { power: 340, efficiency: 17.53 } },
      { name: 'Tiger Pro 72HC 545W', assetType: 'MOD', description: 'Mono PERC bifacial 545W', specifications: { power: 545, efficiency: 21.13 } },
    ],
  },
  {
    name: 'LONGi Solar',
    description: 'Global leader in monocrystalline silicon products, maker of Hi-MO series.',
    website: 'https://www.longi.com',
    contactInfo: { email: 'info@longi.com' },
    models: [
      { name: 'Hi-MO 6 LR5-72HPH 560M', assetType: 'MOD', description: 'Mono PERC bifacial 560W', specifications: { power: 560, efficiency: 21.3 } },
      { name: 'Hi-MO X6 LR5-72HTH 580W', assetType: 'MOD', description: 'HPBC bifacial 580W', specifications: { power: 580, efficiency: 22.0 } },
      { name: 'Hi-MO 5m LR4-72HBD 425W', assetType: 'MOD', description: 'Mono PERC 425W', specifications: { power: 425, efficiency: 20.6 } },
      { name: 'Hi-MO 7 LR5-72HGH 620W', assetType: 'MOD', description: 'N-type bifacial 620W', specifications: { power: 620, efficiency: 22.8 } },
    ],
  },
  {
    name: 'Trina Solar',
    description: 'Leading global module manufacturer with Vertex series advanced technology.',
    website: 'https://www.trinasolar.com',
    contactInfo: { email: 'info@trinasolar.com' },
    models: [
      { name: 'Vertex S+ NEG9R.28 440W', assetType: 'MOD', description: 'N-type i-TOPCon bifacial 440W', specifications: { power: 440, efficiency: 22.5 } },
      { name: 'Vertex DEG21C.20 660W', assetType: 'MOD', description: 'N-type bifacial 660W', specifications: { power: 660, efficiency: 21.6 } },
      { name: 'Vertex S TSM-DE09.08 425W', assetType: 'MOD', description: 'Mono PERC 425W', specifications: { power: 425, efficiency: 21.1 } },
      { name: 'Honey M TSM-300DD14A.08 300W', assetType: 'MOD', description: 'Poly module 300W', specifications: { power: 300, efficiency: 18.3 } },
    ],
  },
  {
    name: 'Canadian Solar',
    description: 'One of the largest solar panel manufacturers globally, known for HiKu and BiKu series.',
    website: 'https://www.canadiansolar.com',
    contactInfo: { email: 'info@canadiansolar.com' },
    models: [
      { name: 'HiKu7 CS7N-655MS 655W', assetType: 'MOD', description: 'Mono PERC bifacial 655W', specifications: { power: 655, efficiency: 21.6 } },
      { name: 'HiKu6 CS6W-555MS 555W', assetType: 'MOD', description: 'Mono PERC bifacial 555W', specifications: { power: 555, efficiency: 21.4 } },
      { name: 'TOPBiKu CS6W-550TB-AG 550W', assetType: 'MOD', description: 'N-type TOPCon bifacial 550W', specifications: { power: 550, efficiency: 21.5 } },
      { name: 'BiKu CS3W-400PB-AG 400W', assetType: 'MOD', description: 'Bifacial Poly 400W', specifications: { power: 400, efficiency: 19.8 } },
    ],
  },
  {
    name: 'JA Solar',
    description: 'Global leader in high-performance solar products with DeepBlue series.',
    website: 'https://www.jasolar.com',
    contactInfo: { email: 'info@jasolar.com' },
    models: [
      { name: 'DeepBlue 4.0 Pro JAM72D40-580/MB 580W', assetType: 'MOD', description: 'N-type bifacial 580W', specifications: { power: 580, efficiency: 22.4 } },
      { name: 'DeepBlue 3.0 JAM72S30-540/MR 540W', assetType: 'MOD', description: 'Mono PERC bifacial 540W', specifications: { power: 540, efficiency: 21.0 } },
      { name: 'DeepBlue 4.0X JAM66D45-675/LB 675W', assetType: 'MOD', description: 'N-type large format 675W', specifications: { power: 675, efficiency: 22.4 } },
    ],
  },
  {
    name: 'REC Group',
    description: 'Premium European solar brand known for high-efficiency twin-peak modules.',
    website: 'https://www.recgroup.com',
    contactInfo: { email: 'sales@recgroup.com' },
    models: [
      { name: 'REC Alpha Pure-RX 430AA 430W', assetType: 'MOD', description: 'N-type heterojunction 430W', specifications: { power: 430, efficiency: 22.3 } },
      { name: 'REC TwinPeak 5 REC380TP5 380W', assetType: 'MOD', description: 'Mono PERC 380W', specifications: { power: 380, efficiency: 20.5 } },
      { name: 'REC Alpha Series 405AA 405W', assetType: 'MOD', description: 'Heterojunction bifacial 405W', specifications: { power: 405, efficiency: 21.7 } },
    ],
  },
  {
    name: 'SunPower',
    description: 'US premium solar panel manufacturer with record-setting Maxeon cell technology.',
    website: 'https://www.sunpower.com',
    models: [
      { name: 'Maxeon 6 SPR-MAX6-460 460W', assetType: 'MOD', description: 'Maxeon IBC bifacial 460W', specifications: { power: 460, efficiency: 22.8 } },
      { name: 'Maxeon 3 SPR-MAX3-400 400W', assetType: 'MOD', description: 'IBC monofacial 400W', specifications: { power: 400, efficiency: 22.6 } },
      { name: 'Performance 6 SPR-P6-500 500W', assetType: 'MOD', description: 'Shingled PERC 500W', specifications: { power: 500, efficiency: 20.4 } },
    ],
  },
  {
    name: 'First Solar',
    description: 'US thin-film CdTe solar module manufacturer for utility-scale projects.',
    website: 'https://www.firstsolar.com',
    models: [
      { name: 'Series 6 Plus FS-6540A 540W', assetType: 'MOD', description: 'CdTe thin-film 540W', specifications: { power: 540, efficiency: 19.3 } },
      { name: 'Series 7 FS-7600 600W', assetType: 'MOD', description: 'CdTe thin-film large format 600W', specifications: { power: 600, efficiency: 19.8 } },
    ],
  },
  {
    name: 'Waaree Energies',
    description: 'India\'s largest solar panel manufacturer with mono PERC and bifacial modules.',
    website: 'https://www.waaree.com',
    contactInfo: { email: 'corporate@waaree.com' },
    models: [
      { name: 'Aditya WS-440 440W', assetType: 'MOD', description: 'Mono PERC 440W', specifications: { power: 440, efficiency: 20.7 } },
      { name: 'Bifacial WSMD-550 550W', assetType: 'MOD', description: 'Mono PERC bifacial 550W', specifications: { power: 550, efficiency: 21.2 } },
      { name: 'Omega 600W', assetType: 'MOD', description: 'Bifacial 600W large format', specifications: { power: 600, efficiency: 21.5 } },
    ],
  },
  {
    name: 'Adani Solar',
    description: 'India\'s major solar module manufacturer with vertically integrated manufacturing.',
    website: 'https://www.adanisolar.com',
    models: [
      { name: 'Mono PERC 540W', assetType: 'MOD', description: 'Mono PERC bifacial 540W', specifications: { power: 540, efficiency: 21.0 } },
      { name: 'TopCon N-type 580W', assetType: 'MOD', description: 'N-type TOPCon 580W', specifications: { power: 580, efficiency: 22.2 } },
    ],
  },
  {
    name: 'Vikram Solar',
    description: 'Leading Indian solar manufacturer known for SOMERA bifacial modules.',
    website: 'https://www.vikramsolar.com',
    models: [
      { name: 'SOMERA Pro 540W', assetType: 'MOD', description: 'Mono PERC bifacial 540W', specifications: { power: 540, efficiency: 21.1 } },
      { name: 'SOMERA Series 600W', assetType: 'MOD', description: 'N-type bifacial 600W', specifications: { power: 600, efficiency: 22.0 } },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  // INV – Inverters
  // ─────────────────────────────────────────────────────────────────────
  {
    name: 'SMA Solar Technology',
    description: 'German inverter manufacturer, global leader in solar inverter technology.',
    website: 'https://www.sma.de',
    contactInfo: { email: 'info@sma.de' },
    models: [
      { name: 'Sunny Tripower CORE2 110kVA', assetType: 'INV', description: 'Three-phase string inverter 110kVA', specifications: { power: 110, efficiency: 98.5 } },
      { name: 'Sunny Central 3000-EV 3000kW', assetType: 'INV', description: 'Central inverter 3000kW for utility-scale', specifications: { power: 3000, efficiency: 98.7 } },
      { name: 'Sunny Highpower PEAK3 100kW', assetType: 'INV', description: 'Three-phase string inverter 100kW', specifications: { power: 100, efficiency: 98.4 } },
      { name: 'Sunny Boy 5.0-SB5.0-1AV-41 5kW', assetType: 'INV', description: 'Residential single-phase 5kW', specifications: { power: 5, efficiency: 97.0 } },
    ],
  },
  {
    name: 'Sungrow Power Supply',
    description: 'World\'s largest inverter company from China with extensive utility and string inverter portfolio.',
    website: 'https://www.sungrowpower.com',
    contactInfo: { email: 'service@sungrowpower.com' },
    models: [
      { name: 'SG250HX 250kW', assetType: 'INV', description: 'String inverter 250kW, 12 MPPTs', specifications: { power: 250, efficiency: 99.0 } },
      { name: 'SG3125HV-MV 3125kW', assetType: 'INV', description: 'Central inverter with integrated MV transformer', specifications: { power: 3125, efficiency: 99.0 } },
      { name: 'SG110CX 110kW', assetType: 'INV', description: 'String inverter 110kW', specifications: { power: 110, efficiency: 98.9 } },
      { name: 'SG5.0RT 5kW', assetType: 'INV', description: 'Residential three-phase 5kW', specifications: { power: 5, efficiency: 98.4 } },
    ],
  },
  {
    name: 'Huawei Solar',
    description: 'Leading digital smart PV inverter and solution provider.',
    website: 'https://solar.huawei.com',
    models: [
      { name: 'SUN2000-215KTL-H3 215kW', assetType: 'INV', description: 'Smart string inverter 215kW', specifications: { power: 215, efficiency: 99.0 } },
      { name: 'SUN2000-100KTL-M2 100kW', assetType: 'INV', description: 'Smart string inverter 100kW', specifications: { power: 100, efficiency: 98.8 } },
      { name: 'SUN2000-3300W-US 3.3kW', assetType: 'INV', description: 'Residential single-phase 3.3kW', specifications: { power: 3.3, efficiency: 97.8 } },
      { name: 'SUN2000-450KTL-H1 450kW', assetType: 'INV', description: 'Smart string inverter 450kW', specifications: { power: 450, efficiency: 99.0 } },
    ],
  },
  {
    name: 'ABB Solar',
    description: 'ABB\'s solar inverter range for residential, commercial, and utility-scale applications.',
    website: 'https://new.abb.com/power-converters-inverters',
    models: [
      { name: 'TRIO-50.0-TL-OUTD 50kW', assetType: 'INV', description: 'Three-phase string inverter 50kW', specifications: { power: 50, efficiency: 98.3 } },
      { name: 'PVS-175-TL 175kW', assetType: 'INV', description: 'Three-phase string inverter 175kW', specifications: { power: 175, efficiency: 98.9 } },
      { name: 'REACT2-UNO-5.0-TL 5kW', assetType: 'INV', description: 'Hybrid inverter with battery 5kW', specifications: { power: 5, efficiency: 97.5 } },
    ],
  },
  {
    name: 'Schneider Electric',
    description: 'Global energy management and automation leader with solar inverter solutions.',
    website: 'https://www.se.com',
    models: [
      { name: 'Conext CL 60kW', assetType: 'INV', description: 'Three-phase grid-tie inverter 60kW', specifications: { power: 60, efficiency: 98.0 } },
      { name: 'Conext TX 100kW', assetType: 'INV', description: 'Three-phase inverter 100kW', specifications: { power: 100, efficiency: 98.2 } },
      { name: 'Xantrex XW+ 6848 6.8kW', assetType: 'INV', description: 'Hybrid inverter/charger 6.8kW', specifications: { power: 6.8, efficiency: 96.0 } },
    ],
  },
  {
    name: 'Fronius',
    description: 'Austrian inverter manufacturer with Symo and Tauro series for commercial applications.',
    website: 'https://www.fronius.com',
    models: [
      { name: 'Fronius Symo Advanced 20.0-3-M 20kW', assetType: 'INV', description: 'Three-phase string inverter 20kW', specifications: { power: 20, efficiency: 98.1 } },
      { name: 'Fronius Tauro 50kW', assetType: 'INV', description: 'Eco three-phase string inverter 50kW', specifications: { power: 50, efficiency: 98.2 } },
      { name: 'Fronius Primo 8.2 8.2kW', assetType: 'INV', description: 'Residential single-phase inverter 8.2kW', specifications: { power: 8.2, efficiency: 98.1 } },
    ],
  },
  {
    name: 'Growatt',
    description: 'Chinese inverter manufacturer widely used in Indian rooftop and utility solar projects.',
    website: 'https://www.growatt.com',
    models: [
      { name: 'MAX 100KTL3 LV 100kW', assetType: 'INV', description: 'Three-phase string inverter 100kW', specifications: { power: 100, efficiency: 98.8 } },
      { name: 'MIN 6000TL-X 6kW', assetType: 'INV', description: 'Single-phase string inverter 6kW', specifications: { power: 6, efficiency: 97.6 } },
      { name: 'MAC 60kTL3-X 60kW', assetType: 'INV', description: 'Three-phase string inverter 60kW', specifications: { power: 60, efficiency: 98.7 } },
    ],
  },
  {
    name: 'Delta Electronics',
    description: 'Taiwanese power and renewable energy company with M50A and M80A inverter series.',
    website: 'https://www.deltaww.com',
    models: [
      { name: 'M50A 50kW', assetType: 'INV', description: 'Three-phase string inverter 50kW', specifications: { power: 50, efficiency: 98.6 } },
      { name: 'M100A 100kW', assetType: 'INV', description: 'Three-phase string inverter 100kW', specifications: { power: 100, efficiency: 98.7 } },
      { name: 'RPI H5A 5kW', assetType: 'INV', description: 'Residential hybrid inverter 5kW', specifications: { power: 5, efficiency: 97.5 } },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  // SCB – String Combiner Boxes
  // ─────────────────────────────────────────────────────────────────────
  {
    name: 'Weidmuller',
    description: 'German industrial connectivity manufacturer with comprehensive PV combiner solutions.',
    website: 'https://www.weidmuller.com',
    models: [
      { name: 'PV SCB 16-String 1000V DC', assetType: 'SCB', description: '16-string combiner box 1000V DC with monitoring', specifications: { voltage: 1000 } },
      { name: 'PV SCB 24-String 1500V DC', assetType: 'SCB', description: '24-string combiner box 1500V DC', specifications: { voltage: 1500 } },
      { name: 'PV SCB 8-String 1000V DC', assetType: 'SCB', description: '8-string combiner box 1000V DC', specifications: { voltage: 1000 } },
    ],
  },
  {
    name: 'Zhenghao Solar',
    description: 'Specialized PV combiner box and DC/AC junction box manufacturer.',
    website: 'https://www.zhsolar.com',
    models: [
      { name: 'ZHPV-16S 1000V 16-String', assetType: 'SCB', description: '16-string DC combiner 1000V', specifications: { voltage: 1000 } },
      { name: 'ZHPV-32S 1500V 32-String', assetType: 'SCB', description: '32-string DC combiner 1500V', specifications: { voltage: 1500 } },
    ],
  },
  {
    name: 'Eaton',
    description: 'US power management company with comprehensive PV balance of system products.',
    website: 'https://www.eaton.com',
    models: [
      { name: 'SC600 16-String Combiner 1000V', assetType: 'SCB', description: '16-input string combiner 1000V DC', specifications: { voltage: 1000 } },
      { name: 'SC600 24-String Combiner 1500V', assetType: 'SCB', description: '24-input string combiner 1500V DC', specifications: { voltage: 1500 } },
    ],
  },
  {
    name: 'Larsen & Toubro',
    description: 'Indian engineering conglomerate with electrical panels and PV balance of system products.',
    website: 'https://www.larsentoubro.com',
    models: [
      { name: 'L&T SCB 12-String 1000V', assetType: 'SCB', description: '12-string DC combiner box 1000V', specifications: { voltage: 1000 } },
      { name: 'L&T SCB 24-String 1500V', assetType: 'SCB', description: '24-string DC combiner box 1500V', specifications: { voltage: 1500 } },
    ],
  },
  {
    name: 'Hitachi ABB Power Grids',
    description: 'Global leader in power grids and electrical equipment.',
    website: 'https://www.hitachiabb-powergrids.com',
    models: [
      { name: 'PVSCB-16-1000V 16-Way', assetType: 'SCB', description: '16-way PV string combiner 1000V', specifications: { voltage: 1000 } },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  // TRF – Transformers
  // ─────────────────────────────────────────────────────────────────────
  {
    name: 'Siemens Energy',
    description: 'German multinational, global leader in energy technology including transformers.',
    website: 'https://www.siemens-energy.com',
    models: [
      { name: 'GEAFOL Cast-Resin 2000kVA', assetType: 'TRF', description: 'Dry-type cast-resin distribution transformer 2000kVA', specifications: { capacity: 2000 } },
      { name: 'Oil-type Distribution 1000kVA', assetType: 'TRF', description: 'Oil-immersed distribution transformer 1000kVA', specifications: { capacity: 1000 } },
      { name: 'Pad-mounted 500kVA 33/0.4kV', assetType: 'TRF', description: 'Pad-mounted transformer 500kVA 33/0.4kV', specifications: { capacity: 500, voltage: 33 } },
    ],
  },
  {
    name: 'ABB Transformers',
    description: 'ABB\'s power transformer division serving utility and renewable energy sectors.',
    website: 'https://new.abb.com/products/transformers',
    models: [
      { name: 'EcoDry 2500kVA', assetType: 'TRF', description: 'Eco-efficient dry-type transformer 2500kVA', specifications: { capacity: 2500 } },
      { name: 'TrafoStar 63000kVA 220kV', assetType: 'TRF', description: 'Large power transformer for grid connection 63MVA', specifications: { capacity: 63000, voltage: 220 } },
    ],
  },
  {
    name: 'Schneider Electric Transformers',
    description: 'Schneider Electric\'s transformer range for industrial and solar applications.',
    website: 'https://www.se.com',
    models: [
      { name: 'Trihal 1600kVA', assetType: 'TRF', description: 'Cast-resin distribution transformer 1600kVA', specifications: { capacity: 1600 } },
      { name: 'Minera 2000kVA Oil Type', assetType: 'TRF', description: 'Oil-immersed distribution transformer 2000kVA', specifications: { capacity: 2000 } },
    ],
  },
  {
    name: 'BHEL (Bharat Heavy Electricals)',
    description: 'India\'s largest power equipment manufacturer with transformers for solar projects.',
    website: 'https://www.bhel.com',
    models: [
      { name: 'Power Transformer 10MVA 33/11kV', assetType: 'TRF', description: 'Oil-immersed power transformer 10MVA', specifications: { capacity: 10000, voltage: 33 } },
      { name: 'Distribution Transformer 500kVA', assetType: 'TRF', description: 'Oil-type distribution transformer 500kVA', specifications: { capacity: 500 } },
      { name: 'Solar Step-up Transformer 2500kVA', assetType: 'TRF', description: 'Solar step-up transformer 2500kVA 0.415/33kV', specifications: { capacity: 2500 } },
    ],
  },
  {
    name: 'Crompton Greaves',
    description: 'Indian power equipment manufacturer, part of CG Power and Industrial Solutions.',
    website: 'https://www.cgpower.com',
    models: [
      { name: 'Distribution Transformer 1000kVA 11kV', assetType: 'TRF', description: 'Oil-immersed distribution transformer', specifications: { capacity: 1000, voltage: 11 } },
      { name: 'Cast Resin Transformer 2000kVA', assetType: 'TRF', description: 'Dry-type cast-resin transformer 2000kVA', specifications: { capacity: 2000 } },
    ],
  },
  {
    name: 'Kirloskar Electric',
    description: 'Leading Indian transformer and motor manufacturer.',
    website: 'https://www.kirloskarelectric.com',
    models: [
      { name: 'Solar Isolation Transformer 2500kVA', assetType: 'TRF', description: 'Solar step-up transformer 2500kVA', specifications: { capacity: 2500 } },
      { name: 'Distribution Transformer 630kVA', assetType: 'TRF', description: 'Oil-type distribution transformer 630kVA', specifications: { capacity: 630 } },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  // CAB – Cables
  // ─────────────────────────────────────────────────────────────────────
  {
    name: 'Nexans',
    description: 'French multinational cable manufacturer with extensive solar PV cable range.',
    website: 'https://www.nexans.com',
    models: [
      { name: 'PV1-F 1x4mm² Solar DC Cable', assetType: 'CAB', description: 'Single core PV solar DC cable 4mm² TÜV certified', specifications: { voltage: 1.8 } },
      { name: 'PV1-F 1x6mm² Solar DC Cable', assetType: 'CAB', description: 'Single core PV solar DC cable 6mm²', specifications: { voltage: 1.8 } },
      { name: 'Nexans TRAY-2X 3-Core AC Cable 95mm²', assetType: 'CAB', description: 'XLPE insulated armoured AC cable 95mm²', specifications: { voltage: 1.1 } },
    ],
  },
  {
    name: 'Prysmian Group',
    description: 'Italian-based world leader in cable manufacturing for energy and telecom.',
    website: 'https://www.prysmiangroup.com',
    models: [
      { name: 'Solar PV Cable 4mm² SOLAXR', assetType: 'CAB', description: 'PV DC cable 4mm² 1.8kV', specifications: { voltage: 1.8 } },
      { name: 'Solar PV Cable 6mm² SOLAXR', assetType: 'CAB', description: 'PV DC cable 6mm² 1.8kV', specifications: { voltage: 1.8 } },
      { name: 'XLPE 3.5 Core 185mm² LT Power Cable', assetType: 'CAB', description: 'LT underground power cable 3.5 core 185mm²', specifications: { voltage: 1.1 } },
      { name: 'HT XLPE 3 Core 95mm² 11kV Cable', assetType: 'CAB', description: 'High tension XLPE cable 3 core 95mm² 11kV', specifications: { voltage: 11 } },
    ],
  },
  {
    name: 'Polycab India',
    description: 'India\'s largest wire and cable manufacturer with comprehensive solar cable range.',
    website: 'https://www.polycab.com',
    contactInfo: { email: 'contactus@polycab.com' },
    models: [
      { name: 'Solar Cable 4mm² TÜV', assetType: 'CAB', description: 'PV DC cable 4mm² TÜV certified 1.8kV', specifications: { voltage: 1.8 } },
      { name: 'Solar Cable 6mm² TÜV', assetType: 'CAB', description: 'PV DC cable 6mm² TÜV certified 1.8kV', specifications: { voltage: 1.8 } },
      { name: 'XLPE Armoured Cable 3.5 Core 120mm²', assetType: 'CAB', description: 'LT armoured power cable 120mm²', specifications: { voltage: 1.1 } },
      { name: 'HT XLPE 3 Core 240mm² 33kV', assetType: 'CAB', description: 'HT cable 3 core 240mm² 33kV', specifications: { voltage: 33 } },
    ],
  },
  {
    name: 'Havells India',
    description: 'Indian electrical equipment manufacturer with solar and industrial cable range.',
    website: 'https://www.havells.com',
    models: [
      { name: 'Solar PV Cable 4mm² 1.8kV', assetType: 'CAB', description: 'DC solar cable 4mm² single core', specifications: { voltage: 1.8 } },
      { name: 'XLPE Cable 3.5 Core 50mm² 1.1kV', assetType: 'CAB', description: 'LT armoured cable 50mm²', specifications: { voltage: 1.1 } },
    ],
  },
  {
    name: 'KEI Industries',
    description: 'Indian wire and cable manufacturer for EHV, HV, and LV applications.',
    website: 'https://www.kei-ind.com',
    models: [
      { name: 'Solar PV Cable 4mm² IEC 62930', assetType: 'CAB', description: 'Solar DC cable 4mm² IEC 62930', specifications: { voltage: 1.8 } },
      { name: 'HT XLPE 3 Core 150mm² 33kV', assetType: 'CAB', description: 'HT cable 3 core 150mm² 33kV', specifications: { voltage: 33 } },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  // MTB – Mounting Structures & Trackers
  // ─────────────────────────────────────────────────────────────────────
  {
    name: 'Nextracker',
    description: 'World\'s largest dedicated solar tracker company spun out of Flex Ltd.',
    website: 'https://www.nextracker.com',
    models: [
      { name: 'NX Horizon Single-Axis Tracker', assetType: 'MTB', description: 'Single-axis solar tracker for utility-scale projects' },
      { name: 'NX Horizon-XTR Terrain-Following', assetType: 'MTB', description: 'Terrain-following single-axis tracker for complex terrain' },
      { name: 'TrueCapture AI-Driven Tracker', assetType: 'MTB', description: 'AI-driven single-axis tracker with energy maximization' },
    ],
  },
  {
    name: 'Array Technologies',
    description: 'US-based single-axis solar tracker manufacturer with DuraTrack series.',
    website: 'https://www.arraytec.com',
    models: [
      { name: 'DuraTrack HZ v3 Single-Axis', assetType: 'MTB', description: 'Single-axis tracker with precision terrain-following' },
      { name: 'SmarTrack Bifacial Optimizer', assetType: 'MTB', description: 'Self-powered tracker controller for bifacial modules' },
    ],
  },
  {
    name: 'Arctech Solar',
    description: 'Chinese solar tracker and fixed mounting structure manufacturer.',
    website: 'https://www.arctechsolar.com',
    models: [
      { name: 'SkyLine II Single-Axis Tracker', assetType: 'MTB', description: 'Single-axis tracker with multi-point drive', specifications: { efficiency: 15 } },
      { name: 'SkySmart II Fixed Tilt Structure', assetType: 'MTB', description: 'Aluminum/galvanised steel fixed mounting structure' },
      { name: 'AgriTrack Agrivoltaic Tracker', assetType: 'MTB', description: 'Elevated single-axis tracker for agrivoltaic projects' },
    ],
  },
  {
    name: 'GameChange Solar',
    description: 'Leading US solar racking and tracker company with Genius Tracker.',
    website: 'https://www.gamechangesolar.com',
    models: [
      { name: 'Genius Tracker Single-Axis', assetType: 'MTB', description: 'Robust single-axis solar tracker with high wind tolerance' },
      { name: 'Fixed-Tilt Racking System', assetType: 'MTB', description: 'Ground-mount fixed-tilt racking for utility-scale' },
    ],
  },
  {
    name: 'Mounting Systems GmbH',
    description: 'German manufacturer of solar mounting structures for rooftop and ground-mount.',
    website: 'https://www.mounting-systems.com',
    models: [
      { name: 'Renusol ConSole+ Flat-Roof', assetType: 'MTB', description: 'Ballasted flat-roof mounting system' },
      { name: 'FS50 Fixed Tilt Ground Mount', assetType: 'MTB', description: 'Ground-mount aluminum structure fixed tilt 25°' },
    ],
  },
  {
    name: 'Holcim (formerly Lafarge)',
    description: 'Building materials company offering ground screws and ballasted mounting solutions.',
    website: 'https://www.holcim.com',
    models: [
      { name: 'Ground Screw Foundation GSA-88', assetType: 'MTB', description: 'Galvanised steel ground screw foundation for solar mounting' },
    ],
  },
  {
    name: 'Pennar Industries',
    description: 'Indian steel structure manufacturer for solar mounting and civil structures.',
    website: 'https://www.pennar.com',
    models: [
      { name: 'Fixed Tilt Steel Structure 10°', assetType: 'MTB', description: 'Hot-dip galvanised fixed tilt mounting structure 10°' },
      { name: 'Fixed Tilt Steel Structure 25°', assetType: 'MTB', description: 'Hot-dip galvanised fixed tilt mounting structure 25°' },
      { name: 'Seasonal Tilt Adjustable Structure', assetType: 'MTB', description: 'Adjustable tilt ground-mount structure' },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  // SWG – Switchgear & Control Panels
  // ─────────────────────────────────────────────────────────────────────
  {
    name: 'ABB Switchgear',
    description: 'ABB\'s comprehensive switchgear portfolio for LV, MV, and HV applications.',
    website: 'https://new.abb.com/products/medium-voltage',
    models: [
      { name: 'UniGear ZS1 MV Switchgear 12kV', assetType: 'SWG', description: 'Metal-enclosed MV switchgear 12kV', specifications: { voltage: 12 } },
      { name: 'SafeRing 36 Ring Main Unit 36kV', assetType: 'SWG', description: 'SF6 ring main unit 36kV', specifications: { voltage: 36 } },
      { name: 'MNS LV Switchboard 690V', assetType: 'SWG', description: 'LV modular switchboard 690V', specifications: { voltage: 0.69 } },
      { name: 'Emax2 ACB 4000A', assetType: 'SWG', description: 'Air circuit breaker 4000A for main incomer', specifications: { capacity: 4000 } },
    ],
  },
  {
    name: 'Siemens Switchgear',
    description: 'Siemens Energy\'s full range of switchgear solutions for solar projects.',
    website: 'https://www.siemens-energy.com',
    models: [
      { name: '8DJ20 MV Switchgear 24kV', assetType: 'SWG', description: 'Compact gas-insulated ring main unit 24kV', specifications: { voltage: 24 } },
      { name: '8BK80 MV Switchgear 12kV', assetType: 'SWG', description: 'Fixed-mounted MV switchgear 12kV', specifications: { voltage: 12 } },
      { name: 'SIVACON S8 LV Switchboard', assetType: 'SWG', description: 'Low-voltage power distribution board 690V', specifications: { voltage: 0.69 } },
    ],
  },
  {
    name: 'Schneider Electric Switchgear',
    description: 'Schneider Electric\'s Prisma and SM6 switchgear for solar energy systems.',
    website: 'https://www.se.com',
    models: [
      { name: 'SM6 MV Switchgear 36kV', assetType: 'SWG', description: 'Modular MV switchgear 36kV', specifications: { voltage: 36 } },
      { name: 'RM6 Ring Main Unit 24kV', assetType: 'SWG', description: 'SF6 ring main unit 24kV', specifications: { voltage: 24 } },
      { name: 'Prisma iP LV Switchboard 415V', assetType: 'SWG', description: 'IP54 LV switchboard 415V for harsh environments', specifications: { voltage: 0.415 } },
      { name: 'Masterpact MTZ2 ACB 2000A', assetType: 'SWG', description: 'Air circuit breaker 2000A', specifications: { capacity: 2000 } },
    ],
  },
  {
    name: 'Eaton Switchgear',
    description: 'Eaton\'s comprehensive MV and LV switchgear for solar power plants.',
    website: 'https://www.eaton.com',
    models: [
      { name: 'XIRIA MV Switchgear 12kV', assetType: 'SWG', description: 'Compact MV gas-insulated switchgear 12kV', specifications: { voltage: 12 } },
      { name: 'Power Defense MCCB 1600A', assetType: 'SWG', description: 'Molded case circuit breaker 1600A', specifications: { capacity: 1600 } },
    ],
  },
  {
    name: 'GE Power',
    description: 'GE\'s power conversion and switchgear solutions for renewable energy projects.',
    website: 'https://www.ge.com/power',
    models: [
      { name: 'MV6 MV Switchgear 12kV', assetType: 'SWG', description: 'Modular MV switchgear 12kV', specifications: { voltage: 12 } },
      { name: 'Entellisys LV Switchboard', assetType: 'SWG', description: 'Intelligent low voltage switchboard with monitoring', specifications: { voltage: 0.415 } },
    ],
  },
  {
    name: 'L&T Switchgear',
    description: 'Larsen & Toubro\'s switchgear division for LV, MV, and industrial applications.',
    website: 'https://www.larsentoubro.com',
    models: [
      { name: 'QO MV Switchgear 11kV', assetType: 'SWG', description: 'Vacuum circuit breaker panel 11kV', specifications: { voltage: 11 } },
      { name: 'Indoswitch MCCB 1000A', assetType: 'SWG', description: 'MCCB 1000A 415V for AC distribution', specifications: { capacity: 1000, voltage: 0.415 } },
      { name: 'PCC/MCC Panel 415V', assetType: 'SWG', description: 'Power control centre / motor control centre 415V', specifications: { voltage: 0.415 } },
    ],
  },
  {
    name: 'Havells Switchgear',
    description: 'Havells comprehensive switchgear and distribution panel range.',
    website: 'https://www.havells.com',
    models: [
      { name: 'SPN Distribution Board 63A', assetType: 'SWG', description: 'Single pole neutral distribution board 63A', specifications: { capacity: 63, voltage: 0.415 } },
      { name: 'ACCL Automatic Changeover Controller', assetType: 'SWG', description: 'Automatic changeover with current limiter', specifications: { voltage: 0.415 } },
    ],
  },
];

export const getAllManufacturerNames = (): string[] =>
  solarManufacturerData.map(m => m.name);

export const getManufacturersByAssetType = (assetType: AssetType): ManufacturerEntry[] =>
  solarManufacturerData.filter(m => m.models.some(model => model.assetType === assetType));

export const getModelsByAssetType = (assetType: AssetType): Array<ModelEntry & { manufacturerName: string }> =>
  solarManufacturerData.flatMap(m =>
    m.models
      .filter(model => model.assetType === assetType)
      .map(model => ({ ...model, manufacturerName: m.name }))
  );

export const getTotalStats = () => ({
  manufacturers: solarManufacturerData.length,
  models: solarManufacturerData.reduce((sum, m) => sum + m.models.length, 0),
  byAssetType: Object.fromEntries(
    (['MOD', 'INV', 'SCB', 'TRF', 'CAB', 'MTB', 'SWG'] as AssetType[]).map(type => [
      type,
      {
        manufacturers: getManufacturersByAssetType(type).length,
        models: getModelsByAssetType(type).length,
      },
    ])
  ),
});
